import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  HiX,
  HiOutlineChatAlt2,
  HiOutlinePhone,
  HiOutlinePaperAirplane,
  HiOutlineDocumentText,
  HiOutlineExclamationCircle,
} from 'react-icons/hi';

const SendWhatsAppModal = ({
  open,
  onClose,
  title = 'Send via WhatsApp',
  documentLabel = 'Document',
  onPreview,
  onSend,
  showPdfStyle = true,
  textOnly = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [filename, setFilename] = useState('');
  const [whatsappConnected, setWhatsappConnected] = useState(true);
  const [renderStyle, setRenderStyle] = useState('default');
  const [previewError, setPreviewError] = useState('');
  const onPreviewRef = useRef(onPreview);
  onPreviewRef.current = onPreview;

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);
    setPreviewError('');
    setPhone('');
    setMessage('');
    setFilename('');
    setRenderStyle('default');

    (async () => {
      try {
        const data = await onPreviewRef.current();
        if (cancelled) return;
        setPhone(data.phone || '');
        setMessage(data.message || '');
        setFilename(data.filename || '');
        setWhatsappConnected(data.whatsapp_connected !== false);
      } catch (err) {
        if (cancelled) return;
        const msg = err.response?.data?.message || err.message || 'Failed to load preview';
        setPreviewError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleSend = async (e) => {
    e.preventDefault();
    const digits = String(phone).replace(/\D/g, '');
    if (digits.length < 10) {
      toast.error('Enter a valid 10-digit phone number');
      return;
    }
    if (!message.trim()) {
      toast.error('Message is required');
      return;
    }
    if (!whatsappConnected) {
      toast.error('WhatsApp is not connected');
      return;
    }

    setSending(true);
    try {
      await onSend({
        phone: digits.length === 10 ? digits : digits.slice(-10),
        message: message.trim(),
        render_style: renderStyle,
      });
      toast.success('Sent via WhatsApp');
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to send via WhatsApp';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-shell" onClick={onClose}>
      <div className="modal-panel max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 sm:p-5 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <HiOutlineChatAlt2 className="w-5 h-5 text-[#25D366]" />
            {title}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <HiX className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSend} className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2">
              <div className="h-8 w-8 rounded-full border-2 border-[#128C7E] border-t-transparent animate-spin" />
              <p className="text-sm text-gray-500">Loading preview…</p>
            </div>
          ) : previewError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {previewError}
            </div>
          ) : (
            <>
              {!whatsappConnected && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex gap-2 text-sm text-amber-900">
                  <HiOutlineExclamationCircle className="w-5 h-5 shrink-0 text-amber-600" />
                  <div>
                    <p className="font-medium">WhatsApp is not connected</p>
                    <p className="mt-1 text-xs text-amber-800">
                      Connect in{' '}
                      <Link to="/whatsapp-settings" className="underline font-medium" onClick={onClose}>
                        Configuration → WhatsApp
                      </Link>{' '}
                      before sending.
                    </p>
                  </div>
                </div>
              )}

              {!textOnly && filename && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 flex items-center gap-2 text-sm text-gray-700">
                  <HiOutlineDocumentText className="w-4 h-4 text-primary-600 shrink-0" />
                  <span>
                    Attachment: <strong>{documentLabel}</strong> ({filename})
                  </span>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-800 mb-1.5">
                  Phone number
                </label>
                <div className="relative">
                  <HiOutlinePhone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50/50 py-2.5 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 transition focus:border-[#128C7E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#128C7E]/25"
                    placeholder="10-digit mobile number"
                  />
                </div>
                <p className="mt-1 text-[11px] text-gray-500">
                  Uses patient WhatsApp, then primary contact, then guardian phone
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-800 mb-1.5">Message</label>
                <textarea
                  rows={8}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 transition focus:border-[#128C7E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#128C7E]/25 resize-none font-mono text-[13px] leading-relaxed"
                  placeholder={textOnly ? 'Message to send on WhatsApp' : 'Message to send with the PDF'}
                />
              </div>

              {showPdfStyle && (
                <div>
                  <label className="block text-xs font-semibold text-gray-800 mb-1.5">PDF style</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setRenderStyle('default')}
                      className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                        renderStyle === 'default'
                          ? 'border-[#128C7E] bg-[#128C7E]/10 text-[#075E54]'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      Default
                    </button>
                    <button
                      type="button"
                      onClick={() => setRenderStyle('letterhead')}
                      className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                        renderStyle === 'letterhead'
                          ? 'border-[#128C7E] bg-[#128C7E]/10 text-[#075E54]'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      Letterhead
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </form>

        <div className="p-4 sm:p-5 border-t border-gray-200 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSend}
            disabled={loading || sending || !!previewError || !whatsappConnected}
            className="w-full sm:w-auto px-4 py-2.5 bg-[#25D366] text-white rounded-lg hover:bg-[#1da851] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <HiOutlinePaperAirplane className="w-4 h-4" />
            {sending ? 'Sending…' : 'Send WhatsApp'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendWhatsAppModal;
